import { useState, useMemo } from "react";

// Supuestos y datos de referencia 2026 (Perú) — valores ilustrativos
const AFPS = [
  { id: "habitat", nombre: "Habitat", comisionFlujo: 0.0147 },
  { id: "integra", nombre: "Integra", comisionFlujo: 0.0155 },
  { id: "prima", nombre: "Prima", comisionFlujo: 0.0160 },
  { id: "profuturo", nombre: "Profuturo", comisionFlujo: 0.0169 },
];

const TIPOS_FONDO = [
  { id: 0, nombre: "Fondo 0 · Protección de capital", rentabilidadAnual: 0.03 },
  { id: 1, nombre: "Fondo 1 · Conservador", rentabilidadAnual: 0.05 },
  { id: 2, nombre: "Fondo 2 · Mixto (por defecto)", rentabilidadAnual: 0.07 },
  { id: 3, nombre: "Fondo 3 · Crecimiento", rentabilidadAnual: 0.09 },
];

const MODALIDADES_PENSION = [
  { id: "retiro_programado", nombre: "Retiro Programado", tasaTecnicaAnual: 0.041 },
  { id: "renta_vitalicia", nombre: "Renta Vitalicia", tasaTecnicaAnual: 0.045 },
  { id: "renta_mixta", nombre: "Renta Mixta", tasaTecnicaAnual: 0.043 },
];

// Esperanza de vida a la edad de jubilación (65) según las tablas de mortalidad
// vigentes de la SBS desde 2019 (persona sana). Fuente: SBS / prensa especializada.
const ESPERANZA_VIDA = { M: 87.5, F: 90.8 };

function formatearSoles(valor) {
  return valor.toLocaleString("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function calcularPensionMensual(saldo, tasaAnual, mesesPension) {
  if (mesesPension <= 0 || saldo <= 0) return 0;
  const tasaMensual = Math.pow(1 + tasaAnual, 1 / 12) - 1;
  if (tasaMensual === 0) return saldo / mesesPension;
  return (saldo * tasaMensual) / (1 - Math.pow(1 + tasaMensual, -mesesPension));
}

export default function CalculadoraAFP() {
  const [modo, setModo] = useState("instantaneo");

  const [edadActual, setEdadActual] = useState(35);
  const [edadRetiro, setEdadRetiro] = useState(65);
  const [sexo, setSexo] = useState("M");
  const [saldoAfp, setSaldoAfp] = useState(40000);
  const [modalidadId, setModalidadId] = useState("retiro_programado");

  const [sueldoBruto, setSueldoBruto] = useState(3000);
  const [afpId, setAfpId] = useState("habitat");
  const [tipoFondoId, setTipoFondoId] = useState(2);

  const modalidad = MODALIDADES_PENSION.find((m) => m.id === modalidadId) ?? MODALIDADES_PENSION[0];
  const afp = AFPS.find((a) => a.id === afpId) ?? AFPS[0];
  const tipoFondo = TIPOS_FONDO.find((f) => f.id === Number(tipoFondoId)) ?? TIPOS_FONDO[2];

  const resultado = useMemo(() => {
    const edad = Number(edadActual) || 0;
    const retiro = Number(edadRetiro) || 65;
    const saldo = Math.max(0, Number(saldoAfp) || 0);
    const esperanzaVida = ESPERANZA_VIDA[sexo] ?? ESPERANZA_VIDA.M;
    const mesesPension = Math.max(0, (esperanzaVida - retiro) * 12);

    if (modo === "instantaneo") {
      const pensionMensual = calcularPensionMensual(saldo, modalidad.tasaTecnicaAnual, mesesPension);
      return {
        saldoUsado: saldo,
        pensionMensual,
        mesesPension,
        esperanzaVida,
        totalEstimadoRecibir: pensionMensual * mesesPension,
      };
    }

    const mesesHastaRetiro = Math.max(0, (retiro - edad) * 12);
    const aporteMensual = (Number(sueldoBruto) || 0) * 0.10;
    const rentabilidadMensualFondo = Math.pow(1 + tipoFondo.rentabilidadAnual, 1 / 12) - 1;

    let saldoProyectado = saldo;
    for (let i = 0; i < mesesHastaRetiro; i++) {
      saldoProyectado = saldoProyectado * (1 + rentabilidadMensualFondo) + aporteMensual;
    }

    const totalAportado = aporteMensual * mesesHastaRetiro;
    const comisionEstimadaPagada = (Number(sueldoBruto) || 0) * afp.comisionFlujo * mesesHastaRetiro;

    const pensionMensual = calcularPensionMensual(saldoProyectado, modalidad.tasaTecnicaAnual, mesesPension);

    return {
      saldoUsado: saldoProyectado,
      pensionMensual,
      mesesPension,
      esperanzaVida,
      totalEstimadoRecibir: pensionMensual * mesesPension,
      mesesHastaRetiro,
      totalAportado,
      comisionEstimadaPagada,
      aporteMensual,
    };
  }, [modo, edadActual, edadRetiro, sexo, saldoAfp, modalidad, sueldoBruto, afp, tipoFondo]);

  const inputClase =
    "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-700/30 focus:border-red-700";
  const botonClase = (activo) =>
    `rounded-lg border py-2.5 text-sm font-medium transition-colors ${
      activo
        ? "border-red-700 bg-red-50 text-red-800"
        : "border-slate-300 text-slate-600 hover:bg-slate-50 bg-white"
    }`;

  return (
    <div className="w-full min-h-full px-4 py-10 bg-stone-50">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center text-lg text-white bg-red-700 rounded-full h-11 w-11">
            🏛️
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900">
              Calculadora AFP
            </h1>
            <p className="text-sm text-slate-500">Perú 2026 · Estimación de pensión de jubilación</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <button type="button" onClick={() => setModo("instantaneo")} className={botonClase(modo === "instantaneo")}>
            ⚡ Instantáneo
          </button>
          <button type="button" onClick={() => setModo("proyeccion")} className={botonClase(modo === "proyeccion")}>
            📊 Proyección
          </button>
        </div>

        <div className="overflow-hidden bg-white border shadow-sm rounded-2xl border-slate-200">
          <div className="p-6 space-y-5 border-b border-slate-100">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Tu edad</label>
                <input
                  type="number"
                  min="18"
                  max="80"
                  value={edadActual}
                  onChange={(e) => setEdadActual(e.target.value)}
                  className={`${inputClase} font-mono`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Edad de retiro</label>
                <input
                  type="number"
                  min="50"
                  max="80"
                  value={edadRetiro}
                  onChange={(e) => setEdadRetiro(e.target.value)}
                  className={`${inputClase} font-mono`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Sexo</label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setSexo("M")} className={botonClase(sexo === "M")}>
                  Hombre
                </button>
                <button type="button" onClick={() => setSexo("F")} className={botonClase(sexo === "F")}>
                  Mujer
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Saldo actual en tu cuenta AFP
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                  S/
                </span>
                <input
                  type="number"
                  min="0"
                  value={saldoAfp}
                  onChange={(e) => setSaldoAfp(e.target.value)}
                  className={`${inputClase} pl-9 font-mono`}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Modalidad de pensión preferida
              </label>
              <select value={modalidadId} onChange={(e) => setModalidadId(e.target.value)} className={inputClase}>
                {MODALIDADES_PENSION.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre} — tasa técnica {(m.tasaTecnicaAnual * 100).toFixed(2)}%
                  </option>
                ))}
              </select>
            </div>

            {modo === "proyeccion" && (
              <div className="pt-4 space-y-4 border-t border-slate-100">
                <p className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                  Datos para proyectar tu fondo
                </p>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Sueldo bruto mensual actual
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-mono text-sm">
                      S/
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={sueldoBruto}
                      onChange={(e) => setSueldoBruto(e.target.value)}
                      className={`${inputClase} pl-9 font-mono`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">AFP actual</label>
                    <select value={afpId} onChange={(e) => setAfpId(e.target.value)} className={inputClase}>
                      {AFPS.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Tipo de fondo</label>
                    <select
                      value={tipoFondoId}
                      onChange={(e) => setTipoFondoId(e.target.value)}
                      className={inputClase}
                    >
                      {TIPOS_FONDO.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 bg-slate-900">
            <p className="mb-1 text-xs tracking-wide uppercase text-slate-400">
              Pensión mensual estimada
            </p>
            <p className="mb-5 font-mono text-4xl font-bold text-white">
              {formatearSoles(Math.max(0, resultado.pensionMensual))}
            </p>

            <div className="space-y-1.5 text-sm">
              {modo === "proyeccion" && (
                <>
                  <div className="flex justify-between text-slate-300">
                    <span>Aporte mensual (10% del sueldo)</span>
                    <span className="font-mono">{formatearSoles(resultado.aporteMensual)}</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Total aportado hasta el retiro</span>
                    <span className="font-mono">{formatearSoles(resultado.totalAportado)}</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Comisión {afp.nombre} estimada pagada</span>
                    <span className="font-mono">{formatearSoles(resultado.comisionEstimadaPagada)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-slate-300 border-t border-slate-700 pt-1.5 mt-1.5">
                <span>Saldo estimado al jubilarte</span>
                <span className="font-mono">{formatearSoles(resultado.saldoUsado)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Esperanza de vida estimada ({sexo === "M" ? "hombre" : "mujer"})</span>
                <span className="font-mono">{resultado.esperanzaVida} años</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Meses de pensión estimados</span>
                <span className="font-mono">{Math.round(resultado.mesesPension)}</span>
              </div>
              <div className="flex justify-between text-slate-300">
                <span>Total estimado a recibir en el retiro</span>
                <span className="font-mono">{formatearSoles(resultado.totalEstimadoRecibir)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-1 text-xs leading-relaxed text-slate-400">
          <p className="font-medium text-slate-500">Antes de tomar decisiones con este resultado:</p>
          <p>
            1. Este cálculo es informativo y referencial. Los montos usan la
            esperanza de vida de las tablas SBS y una tasa técnica de referencia del
            mercado, no un plazo fijo real de pago.
          </p>
          <p>
            2. Los factores exactos de tu pensión los determina la SBS y la
            aseguradora o AFP elegida al momento de tu jubilación, en base a
            probabilidades de supervivencia año a año que no son públicas.
          </p>
          <p>
            3. En el modo Proyección, la rentabilidad del fondo es un supuesto: las
            rentabilidades pasadas no garantizan rentabilidades futuras, y los fondos
            de pensiones están sujetos a riesgo de mercado.
          </p>
          <p>
            4. Para un cálculo oficial, consulta con tu AFP o una entidad financiera
            regulada por la SBS. Esta herramienta no constituye asesoría financiera
            ni previsional.
          </p>
        </div>
      </div>
    </div>
  );
}